import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { Shelf as ShelfData } from '../types/music';
import { TrackCard } from './TrackCard';
interface ShelfProps {
  shelf: ShelfData;
}
export function Shelf({
  shelf
}: ShelfProps) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const nudge = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * Math.max(rail.clientWidth * 0.8, 240),
      behavior: 'smooth'
    });
  };
  return <motion.section aria-labelledby={`shelf-${shelf.id}`} initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.15
  }} transition={{
    duration: 0.28,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-12 first:mt-0" data-id="element-121" data-mp="3bqfhh3mokt05">
      <div className="mb-4 flex items-end justify-between gap-4" data-id="element-122" data-mp="208xnuwytd9rc">
        <div data-id="element-123" data-mp="1w83z61djrqxe">
          <h2 id={`shelf-${shelf.id}`} className="font-display text-2xl text-cream" data-id="element-124" data-mp="1wbrr4w3cg5a0">
            {shelf.title}
          </h2>
          <p className="mt-0.5 text-sm text-muted" data-id="element-125" data-mp="1z9gyme0ozmbu">{shelf.subtitle}</p>
        </div>
        <div className="hidden gap-1 sm:flex" data-id="element-126" data-mp="1z74f3wfad3wo">
          {[{
          dir: -1 as const,
          Icon: ChevronLeftIcon,
          label: `Scroll ${shelf.title} left`
        }, {
          dir: 1 as const,
          Icon: ChevronRightIcon,
          label: `Scroll ${shelf.title} right`
        }].map(({
          dir,
          Icon,
          label
        }) => <button key={label} type="button" onClick={() => nudge(dir)} aria-label={label} className="rounded-full border border-ink-line p-1.5 text-muted transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-127" data-mp="1bi32ry3c18m4">
              <Icon className="h-4 w-4" aria-hidden="true" data-id="element-128" data-mp="sn4vljh6a5xg" />
            </button>)}
        </div>
      </div>
      {/* Vertical padding leaves room for the cards' hover lift without clipping. */}
      <div ref={railRef} className="no-scrollbar -mt-4 flex gap-5 overflow-x-auto px-1 pb-3 pt-4" data-id="element-129" data-mp="2zbdmh3tow1t0">
        {shelf.tracks.map((track, i) => <TrackCard key={track.id} track={track} tracks={shelf.tracks} index={i} data-id="element-130" data-mp="2pipr8dicy5we" data-mpi={i} />)}
      </div>
    </motion.section>;
}
export function ShelfSkeleton({
  title,
  subtitle
}: {
  title: string;
  subtitle: string;
}) {
  return <motion.section initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-12 first:mt-0" data-id="element-131" data-mp="3i7mylll16iad">
      <h2 className="font-display text-2xl text-cream" data-id="element-132" data-mp="3965hq0zsuckm">{title}</h2>
      <p className="mt-0.5 text-sm text-muted" data-id="element-133" data-mp="348wydx2x02hs">{subtitle}</p>
      <div className="mt-4 flex gap-5 overflow-hidden" data-id="element-134" data-mp="34ahsjllbhjf4">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} className="w-40 flex-none sm:w-44" data-id="element-135" data-mp="1xpysnwd7c53z" data-mpi={i}>
            <div className="aspect-square w-full animate-pulse rounded-sm bg-ink-raised" data-id="element-136" data-mp="1ssffz0w5k0e5" />
            <div className="mt-3 h-3 w-4/5 animate-pulse rounded bg-ink-raised" data-id="element-137" data-mp="8c8zxo0qf5bo" />
            <div className="mt-2 h-2.5 w-2/5 animate-pulse rounded bg-ink-raised" data-id="element-138" data-mp="d9hhbme235bv" />
          </div>)}
      </div>
    </motion.section>;
}